/**
 * The demo tenant's Revenue page: the four revenue routes' wire bodies (`modules/revenue`
 * README), built from the same demo engine days as the Overview, so the demo's gross
 * revenue here is the Overview's. Everything the API derives is derived here the way its
 * README says — the weekday-aligned comparison, zero-value orders out of `orders` and AOV,
 * each day's sum shared out exactly over the rows, hours or cells it is made of, real
 * hours across a clock change — so the page reads the demo through the same mappers as
 * production. Discounts, refunds, fees, zero-value orders, payment methods and the
 * channel and customer mixes are seeded shares of each day: DEMO DATA ONLY.
 */

import type {
  BreakdownDimension,
  BreakdownFixedKind,
  BreakdownKind,
  BreakdownRowOut,
  PreviousUnavailable,
  RevenueBasis,
  RevenueBreakdownOut,
  RevenueBreakdownQuery,
  RevenueCompare,
  RevenueGranularity,
  RevenueMetaOut,
  RevenueQuery,
  RevenueSeriesQuery,
  RevenueSummaryOut,
  RevenueTimingOut,
  RevenueSeriesOut,
  SummaryMetricKey,
  SummaryMetricOut,
  TrendBucketOut,
  TrendMarkerOut,
} from '@/api/revenue'
import { BREAKDOWN_FIXED_KINDS, BREAKDOWN_MAX_LIMIT, HOURLY_MAX_DAYS, revenueCompare } from '@/api/revenue'
import { minorUnitExponent } from '@/design/format'
import { ApiError } from '@/lib/api'
import { addDays, dayCount, eachDay, resolvePeriod, type GlobalFilters } from '@/lib/filters'

import { wobble } from '../random'
import { DEMO_TIMEZONE, HOURLY_CURVE, scopeFor, type DemoDataset, type Scope } from './engine'

// --- arithmetic, as the API does it ------------------------------------------------------

/** A fraction to four places. */
function ratio4(numerator: number, denominator: number): number | null {
  return denominator > 0 ? Math.round((numerator / denominator) * 10_000) / 10_000 : null
}

/** Half-up division of whole minor units (away from zero for a negative). */
function divideHalfUp(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null
  const quotient = Math.round(Math.abs(numerator) / denominator)
  return numerator < 0 ? -quotient : quotient
}

/** Whole `total` (either sign) shared by `weights` by the largest remainder, so the parts always sum to it. */
export function shareOut(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((acc, weight) => acc + Math.max(0, weight), 0)
  if (sum <= 0 || weights.length === 0) return weights.map((_, index) => (index === 0 ? total : 0))
  const magnitude = Math.abs(total)
  const exact = weights.map((weight) => (magnitude * Math.max(0, weight)) / sum)
  const parts = exact.map(Math.floor)
  let left = magnitude - parts.reduce((acc, part) => acc + part, 0)
  const order = exact.map((value, index) => ({ index, rest: value - Math.floor(value) })).sort((a, b) => b.rest - a.rest)
  for (const { index } of order) {
    if (left <= 0) break
    parts[index] = (parts[index] ?? 0) + 1
    left -= 1
  }
  return total < 0 ? parts.map((part) => -part) : parts
}

function deltaOf(value: number | null, previous: number | null) {
  if (value === null || previous === null) return null
  const absolute = Math.round((value - previous) * 10_000) / 10_000
  return { absolute, ratio: previous === 0 ? null : Math.round(((value - previous) / Math.abs(previous)) * 10_000) / 10_000 }
}

// --- the tenant's clock (Europe/London, the demo's zone) -----------------------------------

const HOUR_MS = 3_600_000

function lastSunday(year: number, month: number): number {
  const last = new Date(Date.UTC(year, month + 1, 0))
  return last.getUTCDate() - last.getUTCDay()
}

/** The UK's offset from UTC, in hours, at a UTC instant: BST from 01:00 UTC on March's last Sunday to 01:00 UTC on October's. */
export function londonOffsetHours(utcMs: number): number {
  const year = new Date(utcMs).getUTCFullYear()
  const start = Date.UTC(year, 2, lastSunday(year, 2), 1)
  const end = Date.UTC(year, 9, lastSunday(year, 9), 1)
  return utcMs >= start && utcMs < end ? 1 : 0
}

/** The UTC instant of a tenant day's midnight (never in a clock change: those are at 01:00 UTC). */
function midnightUtc(day: string): number {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number]
  const guess = Date.UTC(year, month - 1, date)
  return guess - londonOffsetHours(guess - HOUR_MS) * HOUR_MS
}

/** An instant as the API writes it: the tenant's wall clock and offset (`+01:00`, or `Z` in GMT). */
export function tenantInstant(utcMs: number): string {
  const offset = londonOffsetHours(utcMs)
  const wall = new Date(utcMs + offset * HOUR_MS).toISOString().slice(0, 19)
  return `${wall}${offset ? '+01:00' : 'Z'}`
}

/** A tenant day's real hours: 24, or 23 / 25 on the days the clocks change. */
export function hoursOf(day: string): { start: number; wallHour: number }[] {
  const from = midnightUtc(day)
  const to = midnightUtc(addDays(day, 1))
  const hours: { start: number; wallHour: number }[] = []
  for (let start = from; start < to; start += HOUR_MS) {
    hours.push({ start, wallHour: new Date(start + londonOffsetHours(start) * HOUR_MS).getUTCHours() })
  }
  return hours
}

/** The UTC instant of a wall-clock hour on a tenant day (one the clocks neither skip nor repeat). */
export function wallClock(day: string, hour: number): number {
  const match = hoursOf(day).find((candidate) => candidate.wallHour === hour)
  return match ? match.start : midnightUtc(day) + hour * HOUR_MS
}

/** Monday 0. */
function weekdayOf(day: string): number {
  return (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7
}

// --- one day's commerce ---------------------------------------------------------------------

interface DayFacts {
  date: string
  gross: number
  netBeforeRefunds: number
  refunds: number
  refundCount: number
  /** Zero-value orders excluded. */
  orders: number
  zeroValue: number
  tickets: number
  /** Σ competition lines' totals: the day's gross less its fees. */
  ticketLines: number
  fees: number
  /** Competition id (or `ARCHIVED`) → its lines' total, tickets and orders. */
  competitions: Map<string, { lines: number; tickets: number; orders: number }>
  newShare: number
}

/** The 10-channel keys the demo credits revenue to, with their taxonomy labels and weights. */
const DEMO_CHANNELS: readonly { key: string; label: string; weight: number }[] = [
  { key: 'paid_social', label: 'Paid social', weight: 0.34 },
  { key: 'direct', label: 'Direct', weight: 0.17 },
  { key: 'email', label: 'Email', weight: 0.15 },
  { key: 'organic_social', label: 'Organic social', weight: 0.11 },
  { key: 'paid_search', label: 'Paid search', weight: 0.08 },
  { key: 'referral', label: 'Referral', weight: 0.04 },
  { key: 'organic_search', label: 'Organic search', weight: 0.03 },
  { key: 'sms', label: 'SMS', weight: 0.02 },
]
/** Of all revenue, what no touch is credited for. */
const UNATTRIBUTED_WEIGHT = 0.05
const UNCLASSIFIED_WEIGHT = 0.01

const DEMO_CAMPAIGNS: readonly { key: string; weight: number }[] = [
  { key: 'Supercar September', weight: 0.22 },
  { key: 'iPhone 17 launch', weight: 0.16 },
  { key: 'Autumn cash giveaway', weight: 0.12 },
  { key: 'Weekly newsletter', weight: 0.1 },
  { key: 'Matchday venue takeover', weight: 0.04 },
]

const DEMO_PAYMENT_METHODS: readonly { key: string; label: string; weight: number }[] = [
  { key: 'stripe:card', label: 'Stripe · card', weight: 0.57 },
  { key: 'stripe:apple_pay', label: 'Stripe · apple_pay', weight: 0.18 },
  { key: 'stripe:google_pay', label: 'Stripe · google_pay', weight: 0.09 },
  { key: 'dna:card', label: 'DNA Payments · card', weight: 0.11 },
  { key: 'paypal:wallet', label: 'PayPal · wallet', weight: 0.04 },
]
const UNKNOWN_PAYMENT_WEIGHT = 0.01

/** How each model credits the channels: first touch favours discovery, last touch the closer, paid click the paid channels. */
const MODEL_TILT: Record<string, Readonly<Record<string, number>>> = {
  first_touch: { paid_social: 1.25, organic_social: 1.3, direct: 0.6, email: 0.7 },
  last_touch: { direct: 1.6, email: 1.2, paid_social: 0.8 },
  last_non_direct: {},
  paid_click: { paid_social: 1.15, paid_search: 1.1, email: 0.9 },
}

/** The fixed rows' labels, as the API gives them (`definitions.py` `FIXED_ROW_LABELS`). */
const FIXED_LABELS: Record<BreakdownFixedKind, string> = {
  other: 'Other',
  fees_and_other: 'Fees and other lines',
  unsynced_competition: 'Competition not synced yet',
  uncategorised: 'Uncategorised',
  unattributed: 'Unattributed',
  unclassified: 'Not classified yet',
  no_campaign: 'No campaign',
  unknown: 'Unknown',
  unmatched_refunds: 'Refunds of orders not synced',
}

/** Earlier draws the demo fixture does not list, which its unlisted demand is credited to. */
const PAST_COMPETITIONS: readonly { key: string; name: string; category: string; weight: number }[] = [
  { key: 'cmp_past_audi', name: 'Audi RS6 Avant', category: 'Cars', weight: 0.24 },
  { key: 'cmp_past_cash20k', name: '£20,000 Tax-Free Cash', category: 'Cash', weight: 0.2 },
  { key: 'cmp_past_golf', name: 'VW Golf R', category: 'Cars', weight: 0.16 },
  { key: 'cmp_past_ipad', name: 'iPad Pro bundle', category: 'Tech', weight: 0.12 },
  { key: 'cmp_past_omega', name: 'Omega Seamaster', category: 'Watches', weight: 0.1 },
  { key: 'cmp_past_maldives', name: 'Maldives for Two', category: 'Travel', weight: 0.08 },
  { key: 'cmp_past_ebike', name: 'Electric bike', category: 'Bikes', weight: 0.06 },
]
const UNSYNCED_WEIGHT = 0.04

/** As the API labels them (`definitions.py` `CUSTOMER_TYPE_LABELS`). */
const CUSTOMER_TYPE_LABELS = { new: 'New customers', returning: 'Returning customers', guest: 'Guest checkouts' } as const
const GUEST_SHARE = 0.12

interface Part {
  key: string
  kind: BreakdownKind
  label: string
  weight: number
  /** Competition lines' tickets, for the parts that are competitions (or their categories). */
  tickets?: number
}

interface RowTotals {
  key: string
  kind: BreakdownKind
  label: string
  revenue: number
  orders: number
  tickets: number
}

export class MockRevenue {
  private readonly unit: number
  private readonly facts = new Map<string, DayFacts>()

  constructor(
    private readonly dataset: DemoDataset,
    private readonly currency: string,
  ) {
    this.unit = 10 ** minorUnitExponent(currency)
  }

  // --- framing ----------------------------------------------------------------------------

  private frame(filters: GlobalFilters) {
    const { current } = resolvePeriod(filters, this.dataset.now)
    const days = dayCount(current.from, current.to)
    const compare: RevenueCompare = revenueCompare(filters)
    let previous: { from: string; to: string } | null = null
    let unavailable: PreviousUnavailable | null = null
    if (compare === 'none') unavailable = 'not_compared'
    else {
      const shift = compare === 'previous_period' ? 7 * Math.ceil(days / 7) : 364
      previous = { from: addDays(current.from, -shift), to: addDays(current.to, -shift) }
      if (compare === 'same_period_last_year' && days > 364) unavailable = 'range_overlaps_comparison'
    }
    return { range: { from: current.from, to: current.to }, previous, unavailable, compare, days }
  }

  private meta(filters: GlobalFilters, basis: RevenueBasis, frame: ReturnType<MockRevenue['frame']>): RevenueMetaOut {
    const environments = filters.environments.length > 0 ? [...filters.environments] : ['production' as const]
    return {
      currency: this.currency,
      timezone: DEMO_TIMEZONE,
      range: frame.range,
      previous_range: frame.previous,
      basis,
      compare: frame.compare,
      previous_unavailable: frame.unavailable,
      freshness: { latest_source_updated_at: new Date(this.dataset.now.getTime() - 4 * 60_000).toISOString() },
      source: 'live',
      environments,
      non_production: environments.some((environment) => environment !== 'production'),
      website_id: filters.dimensions.website ?? null,
    }
  }

  /** The comparison's days, or none when it is not read. */
  private previousDays(frame: ReturnType<MockRevenue['frame']>): string[] | null {
    return frame.previous && !frame.unavailable ? eachDay({ ...frame.previous, days: frame.days }) : null
  }

  // --- the days ---------------------------------------------------------------------------

  private day(date: string, scope: Scope): DayFacts {
    const key = `${date}|${JSON.stringify(scope)}`
    const cached = this.facts.get(key)
    if (cached) return cached
    const seed = this.dataset.tenant.seed
    const totals = this.dataset.day(date, scope)
    const competitions = new Map<string, { lines: number; tickets: number; orders: number }>()
    for (const [id, competition] of totals.competitions) {
      competitions.set(id, { lines: Math.round(competition.revenue * this.unit), tickets: competition.tickets, orders: competition.orders })
    }
    const ticketLines = [...competitions.values()].reduce((sum, competition) => sum + competition.lines, 0)
    const fees = Math.round(ticketLines * 0.015 * wobble(seed, 0.3, 'rv-fees', date))
    const gross = ticketLines + fees
    const discounts = Math.round(gross * 0.045 * wobble(seed, 0.4, 'rv-discount', date))
    const refunds = Math.round(gross * 0.009 * wobble(seed, 0.8, 'rv-refund', date))
    const zeroValue = Math.round(totals.orders * 0.015 * wobble(seed, 0.5, 'rv-zero', date))
    const facts: DayFacts = {
      date,
      gross,
      netBeforeRefunds: gross - discounts,
      refunds,
      refundCount: refunds > 0 ? Math.max(1, Math.round(refunds / (6 * this.unit))) : 0,
      orders: Math.max(0, totals.orders - zeroValue),
      zeroValue: Math.min(zeroValue, totals.orders),
      tickets: totals.tickets,
      ticketLines,
      fees,
      competitions,
      newShare: totals.newCustomers + totals.returningCustomers > 0 ? totals.newCustomers / (totals.newCustomers + totals.returningCustomers) : 0.3,
    }
    this.facts.set(key, facts)
    return facts
  }

  private revenueOf(day: DayFacts, basis: RevenueBasis): number {
    return basis === 'gross' ? day.gross : day.netBeforeRefunds - day.refunds
  }

  private days(dates: readonly string[] | null, scope: Scope): DayFacts[] {
    return (dates ?? []).map((date) => this.day(date, scope))
  }

  // --- summary ----------------------------------------------------------------------------

  summary({ filters, basis }: RevenueQuery): RevenueSummaryOut {
    const frame = this.frame(filters)
    const scope = scopeFor(this.dataset.tenant, filters)
    const current = this.figures(this.days(eachDay({ ...frame.range, days: frame.days }), scope), basis)
    const previousDays = this.previousDays(frame)
    const previous = previousDays ? this.figures(this.days(previousDays, scope), basis) : null

    const metrics = Object.fromEntries(
      (Object.keys(current) as SummaryMetricKey[]).map((key) => {
        const value = current[key]
        const before = previous ? previous[key] : null
        return [key, this.metric(key, value, before)]
      }),
    ) as Record<SummaryMetricKey, SummaryMetricOut>

    const concentrationOf = (dates: string[] | null) => {
      if (!dates) return null
      const competitions = this.rows('competition', dates, scope, basis, 'last_non_direct')
      const channels = this.rows('channel', dates, scope, basis, 'last_non_direct')
      const total = competitions.reduce((sum, row) => sum + row.revenue, 0)
      const topFive = competitions
        .filter((row) => row.kind === 'value')
        .map((row) => row.revenue)
        .sort((a, b) => b - a)
        .slice(0, 5)
        .reduce((sum, revenue) => sum + revenue, 0)
      const topChannel = channels.filter((row) => row.kind === 'value').sort((a, b) => b.revenue - a.revenue)[0] ?? null
      const orders = this.days(dates, scope).reduce((sum, day) => sum + day.orders, 0)
      const customers = Math.round(orders * this.dataset.tenant.commerce.customersPerOrder * (1 - GUEST_SHARE) * 0.55)
      return {
        total,
        topFive: ratio4(topFive, total),
        topChannel: topChannel && total > 0 ? { share: ratio4(topChannel.revenue, total), channel: topChannel.key } : null,
        customers,
        decile: customers >= 20 && total > 0 ? Math.round(0.47 * wobble(this.dataset.tenant.seed, 0.08, 'rv-decile', dates[0] ?? '') * 10_000) / 10_000 : null,
      }
    }
    const now = concentrationOf(eachDay({ ...frame.range, days: frame.days }))
    const before = concentrationOf(previousDays)
    const noRevenue = (total: number | undefined) => (total !== undefined && total <= 0 ? 'no_revenue' : null)

    return {
      data: {
        basis,
        compare: frame.compare,
        metrics,
        not_available: { revenue_per_visitor: 'needs_browser_events' },
        concentration: {
          top_decile_customers_share: {
            unit: 'ratio',
            value: now?.decile ?? null,
            previous: before?.decile ?? null,
            null_reason: now && now.decile === null ? (now.customers < 20 ? 'too_few_customers' : 'no_revenue') : null,
            previous_null_reason: before && before.decile === null ? (before.customers < 20 ? 'too_few_customers' : 'no_revenue') : null,
            customers: now?.customers ?? 0,
            previous_customers: before?.customers ?? null,
            guest_share: now && now.total > 0 ? GUEST_SHARE : null,
            previous_guest_share: before && before.total > 0 ? GUEST_SHARE : null,
          },
          top_5_competitions_share: {
            unit: 'ratio',
            value: now?.topFive ?? null,
            previous: before?.topFive ?? null,
            null_reason: noRevenue(now?.total),
            previous_null_reason: noRevenue(before?.total),
          },
          top_channel_share: {
            unit: 'ratio',
            value: now?.topChannel?.share ?? null,
            previous: before?.topChannel?.share ?? null,
            null_reason: noRevenue(now?.total) ?? (now && !now.topChannel ? 'no_attributed_revenue' : null),
            previous_null_reason: noRevenue(before?.total) ?? (before && !before.topChannel ? 'no_attributed_revenue' : null),
            channel: (now?.topChannel?.channel as RevenueSummaryOut['data']['concentration']['top_channel_share']['channel']) ?? null,
            previous_channel: before?.topChannel?.channel ?? null,
          },
        },
        site_credit: { status: 'included_unsplit', reason: 'awaiting_connector_split' },
      },
      meta: this.meta(filters, basis, frame),
    }
  }

  private figures(days: readonly DayFacts[], basis: RevenueBasis): Record<SummaryMetricKey, number | null> {
    const sum = (pick: (day: DayFacts) => number) => days.reduce((acc, day) => acc + pick(day), 0)
    const gross = sum((day) => day.gross)
    const netBefore = sum((day) => day.netBeforeRefunds)
    const refunds = sum((day) => day.refunds)
    const orders = sum((day) => day.orders)
    const tickets = sum((day) => day.tickets)
    const revenue = basis === 'gross' ? gross : netBefore - refunds
    // Free entries' tickets stay in tickets sold but out of tickets per order (decision 3).
    const paidTickets = sum((day) => Math.round(day.tickets * (day.orders / Math.max(1, day.orders + day.zeroValue))))
    return {
      revenue_gross: gross,
      revenue_net: netBefore - refunds,
      revenue_net_before_refunds: netBefore,
      discounts: gross - netBefore,
      orders,
      orders_zero_value: sum((day) => day.zeroValue),
      aov: divideHalfUp(revenue, orders),
      tickets_sold: tickets,
      average_ticket_price: divideHalfUp(sum((day) => day.ticketLines), tickets),
      tickets_per_order: ratio4(paidTickets, orders),
      refunds,
      refund_count: sum((day) => day.refundCount),
      refund_rate: ratio4(refunds, gross),
      revenue_per_visitor: null,
    }
  }

  private metric(key: SummaryMetricKey, value: number | null, previous: number | null): SummaryMetricOut {
    const unit = key === 'tickets_per_order' || key === 'refund_rate' ? 'ratio' : COUNTS.has(key) ? 'count' : 'currency_minor'
    const reason = (figure: number | null) => (figure !== null || key === 'revenue_per_visitor' ? null : NULL_REASONS[key] ?? null)
    return { unit, value, previous, delta: deltaOf(value, previous), null_reason: reason(value), previous_null_reason: previous === null ? null : reason(previous) }
  }

  // --- trend --------------------------------------------------------------------------------

  trend({ filters, basis, granularity }: RevenueSeriesQuery): RevenueSeriesOut {
    const frame = this.frame(filters)
    if (granularity === 'hour' && frame.days > HOURLY_MAX_DAYS) {
      // The API's answer to hours over more than two days: a 422 that says so.
      throw new ApiError(422, 'validation_error', `granularity=hour covers at most ${HOURLY_MAX_DAYS} days`, undefined, {
        granularity: 'hour',
        max_days: HOURLY_MAX_DAYS,
        days: frame.days,
      })
    }
    const scope = scopeFor(this.dataset.tenant, filters)
    const dates = eachDay({ ...frame.range, days: frame.days })
    const previousDates = this.previousDays(frame)
    const days = this.days(dates, scope)
    const previousDays = previousDates ? this.days(previousDates, scope) : null

    const buckets = granularity === 'hour' ? this.hourBuckets(days, previousDays, basis) : this.dayBuckets(granularity, days, previousDays, basis)
    const totalsOf = (list: readonly DayFacts[]) => ({
      revenue: list.reduce((sum, day) => sum + this.revenueOf(day, basis), 0),
      orders: list.reduce((sum, day) => sum + day.orders, 0),
      tickets: list.reduce((sum, day) => sum + day.tickets, 0),
    })

    return {
      data: {
        basis,
        compare: frame.compare,
        granularity,
        buckets,
        totals: totalsOf(days),
        previous_totals: previousDays ? totalsOf(previousDays) : null,
        markers: this.markers(frame.range),
      },
      meta: this.meta(filters, basis, frame),
    }
  }

  /** Day, week (seven days from `from`) or month (calendar months, cut to the range) buckets, each beside the same days of the comparison. */
  private dayBuckets(granularity: Exclude<RevenueGranularity, 'hour'>, days: DayFacts[], previous: DayFacts[] | null, basis: RevenueBasis): TrendBucketOut[] {
    const groups: DayFacts[][] = []
    days.forEach((day, index) => {
      const startsNew =
        index === 0 || granularity === 'day' || (granularity === 'week' ? index % 7 === 0 : day.date.slice(0, 7) !== days[index - 1]?.date.slice(0, 7))
      if (startsNew) groups.push([day])
      else groups[groups.length - 1]?.push(day)
    })
    let offset = 0
    return groups.map((group) => {
      const first = group[0] as DayFacts
      const last = group[group.length - 1] as DayFacts
      const before = previous?.slice(offset, offset + group.length) ?? null
      offset += group.length
      return {
        start: tenantInstant(midnightUtc(first.date)),
        end: tenantInstant(midnightUtc(addDays(last.date, 1))),
        first_day: first.date,
        last_day: last.date,
        revenue: group.reduce((sum, day) => sum + this.revenueOf(day, basis), 0),
        orders: group.reduce((sum, day) => sum + day.orders, 0),
        tickets: group.reduce((sum, day) => sum + day.tickets, 0),
        previous_start: before?.[0] ? tenantInstant(midnightUtc(before[0].date)) : null,
        previous_revenue: before ? before.reduce((sum, day) => sum + this.revenueOf(day, basis), 0) : null,
      }
    })
  }

  /** The day's hours, each its share of the day by the demo's hourly curve; a repeated or skipped hour has no comparison. */
  private hourParts(day: DayFacts, basis: RevenueBasis) {
    const hours = hoursOf(day.date)
    const weights = hours.map((hour) => HOURLY_CURVE[hour.wallHour] ?? 0)
    const revenue = shareOut(this.revenueOf(day, basis), weights)
    const orders = shareOut(day.orders, weights)
    const tickets = shareOut(day.tickets, weights)
    return hours.map((hour, index) => ({ ...hour, revenue: revenue[index] ?? 0, orders: orders[index] ?? 0, tickets: tickets[index] ?? 0 }))
  }

  private hourBuckets(days: DayFacts[], previous: DayFacts[] | null, basis: RevenueBasis): TrendBucketOut[] {
    return days.flatMap((day, index) => {
      const parts = this.hourParts(day, basis)
      const before = previous?.[index] ? this.hourParts(previous[index], basis) : null
      const once = (list: { wallHour: number }[], hour: number) => list.filter((part) => part.wallHour === hour).length === 1
      return parts.map((part) => {
        const match = before && once(parts, part.wallHour) && once(before, part.wallHour) ? before.find((other) => other.wallHour === part.wallHour) : undefined
        return {
          start: tenantInstant(part.start),
          end: tenantInstant(part.start + HOUR_MS),
          first_day: day.date,
          last_day: day.date,
          revenue: part.revenue,
          orders: part.orders,
          tickets: part.tickets,
          previous_start: match ? tenantInstant(match.start) : null,
          previous_revenue: match ? match.revenue : null,
        }
      })
    })
  }

  /** Competitions launching (midday) or drawing (21:00) inside the range, earliest first. */
  private markers(range: { from: string; to: string }): TrendMarkerOut[] {
    const markers: TrendMarkerOut[] = []
    for (const competition of this.dataset.tenant.competitions) {
      const { opens, closes } = this.dataset.schedule(competition)
      if (range.from <= opens && opens <= range.to) {
        markers.push({ type: 'launch', at: tenantInstant(wallClock(opens, 12)), source: 'start_at', competition_id: competition.id, name: competition.name })
      }
      if (range.from <= closes && closes <= range.to) {
        markers.push({ type: 'draw', at: tenantInstant(wallClock(closes, 21)), source: 'draw_at', competition_id: competition.id, name: competition.name })
      }
    }
    return markers.sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
  }

  // --- breakdown ------------------------------------------------------------------------------

  breakdown(query: RevenueBreakdownQuery): RevenueBreakdownOut {
    const { filters, basis, by } = query
    const limit = Math.min(BREAKDOWN_MAX_LIMIT, Math.max(1, query.limit ?? 10))
    const model = by === 'channel' || by === 'campaign' ? (query.model ?? 'last_non_direct') : null
    const windowed: GlobalFilters = {
      ...filters,
      ...(query.window ? { range: 'custom' as const, from: query.window.from, to: query.window.to } : {}),
      ...(query.withoutComparison ? { compare: 'none' as const } : {}),
    }
    const frame = this.frame(windowed)
    const scope = scopeFor(this.dataset.tenant, filters)
    const current = this.rows(by, eachDay({ ...frame.range, days: frame.days }), scope, basis, model ?? 'last_non_direct')
    const previousDays = this.previousDays(frame)
    const previous = previousDays ? this.rows(by, previousDays, scope, basis, model ?? 'last_non_direct') : null
    const total = current.reduce((sum, row) => sum + row.revenue, 0)
    const previousTotal = previous ? previous.reduce((sum, row) => sum + row.revenue, 0) : null
    const previousOf = (key: string) => (previous ? (previous.find((row) => row.key === key)?.revenue ?? 0) : null)

    // Values best first, `limit` of them; the rest one `other` row; then the fixed rows in their order.
    const values = current.filter((row) => row.kind === 'value')
    if (by !== 'customer_type') values.sort((a, b) => b.revenue - a.revenue || (previousOf(b.key) ?? 0) - (previousOf(a.key) ?? 0) || a.key.localeCompare(b.key))
    const kept = by === 'customer_type' ? values : values.slice(0, limit)
    const folded = by === 'customer_type' ? [] : values.slice(limit)
    const fixed = BREAKDOWN_FIXED_KINDS.flatMap((kind) => current.filter((row) => row.kind === kind))
    const rows: RowTotals[] = [...kept]
    if (folded.length > 0) {
      rows.push({
        key: 'other',
        kind: 'other',
        label: FIXED_LABELS.other,
        revenue: folded.reduce((sum, row) => sum + row.revenue, 0),
        orders: folded.reduce((sum, row) => sum + row.orders, 0),
        tickets: folded.reduce((sum, row) => sum + row.tickets, 0),
      })
    }
    rows.push(...fixed)
    const foldedPrevious = previous ? folded.reduce((sum, row) => sum + (previousOf(row.key) ?? 0), 0) : null
    const shareNull = total <= 0 ? ('no_revenue' as const) : null

    const out: BreakdownRowOut[] = rows
      .filter((row) => row.kind !== 'value' || row.revenue !== 0 || row.orders > 0 || row.tickets > 0 || (previousOf(row.key) ?? 0) !== 0 || by === 'customer_type')
      .map((row) => {
        const before = row.kind === 'other' ? foldedPrevious : previousOf(row.key)
        const aov = divideHalfUp(row.revenue, row.orders)
        return {
          key: row.key,
          kind: row.kind,
          label: row.label,
          revenue: row.revenue,
          share: shareNull ? null : ratio4(row.revenue, total),
          orders: row.orders,
          aov,
          aov_null_reason: aov === null ? 'no_orders' : null,
          tickets: row.tickets,
          previous_revenue: before,
          delta: deltaOf(row.revenue, before),
        }
      })

    return {
      data: {
        by,
        basis,
        compare: frame.compare,
        model,
        limit,
        rows: out,
        total: {
          revenue: total,
          orders: this.days(eachDay({ ...frame.range, days: frame.days }), scope).reduce((sum, day) => sum + day.orders, 0),
          tickets: current.reduce((sum, row) => sum + row.tickets, 0),
          previous_revenue: previousTotal,
        },
        share_null_reason: shareNull,
      },
      meta: this.meta(windowed, basis, frame),
    }
  }

  /** Every row the dimension has over `dates`, each day's revenue shared out over the day's parts. */
  private rows(by: BreakdownDimension, dates: readonly string[], scope: Scope, basis: RevenueBasis, model: string): RowTotals[] {
    const totals = new Map<string, RowTotals>()
    for (const date of dates) {
      const day = this.day(date, scope)
      const parts = this.parts(by, day, model)
      const weights = parts.map((part) => part.weight)
      const revenue = shareOut(this.revenueOf(day, basis), weights)
      const orders = shareOut(day.orders, weights)
      const tickets = by === 'competition' || by === 'prize_category' ? parts.map((part) => part.tickets ?? 0) : shareOut(day.tickets, weights)
      parts.forEach((part, index) => {
        const row = totals.get(part.key) ?? { key: part.key, kind: part.kind, label: part.label, revenue: 0, orders: 0, tickets: 0 }
        row.revenue += revenue[index] ?? 0
        row.orders += orders[index] ?? 0
        row.tickets += tickets[index] ?? 0
        totals.set(part.key, row)
      })
    }
    return [...totals.values()]
  }

  private parts(by: BreakdownDimension, day: DayFacts, model: string): Part[] {
    const seed = this.dataset.tenant.seed
    const fees: Part = { key: 'fees_and_other', kind: 'fees_and_other', label: FIXED_LABELS.fees_and_other, weight: day.fees }
    switch (by) {
      case 'competition':
      case 'prize_category': {
        const parts = new Map<string, Part>()
        const add = (part: Part, lines: number, tickets: number) => {
          const existing = parts.get(part.key) ?? { ...part, weight: 0, tickets: 0 }
          existing.weight += lines
          existing.tickets = (existing.tickets ?? 0) + tickets
          parts.set(part.key, existing)
        }
        for (const [id, competition] of day.competitions) {
          const fixture = this.dataset.tenant.competitions.find((candidate) => candidate.id === id)
          if (fixture) {
            add(by === 'competition' ? { key: fixture.id, kind: 'value', label: fixture.name, weight: 0 } : { key: fixture.category, kind: 'value', label: fixture.category, weight: 0 }, competition.lines, competition.tickets)
            continue
          }
          // Demand for competitions the fixture does not list: mostly earlier draws, a little not synced yet.
          const shares = [...PAST_COMPETITIONS.map((past) => past.weight), UNSYNCED_WEIGHT]
          const lines = shareOut(competition.lines, shares)
          const tickets = shareOut(competition.tickets, shares)
          PAST_COMPETITIONS.forEach((past, index) =>
            add(
              by === 'competition' ? { key: past.key, kind: 'value', label: past.name, weight: 0 } : { key: past.category, kind: 'value', label: past.category, weight: 0 },
              lines[index] ?? 0,
              tickets[index] ?? 0,
            ),
          )
          add(
            by === 'competition'
              ? { key: 'unsynced_competition', kind: 'unsynced_competition', label: FIXED_LABELS.unsynced_competition, weight: 0 }
              : { key: 'uncategorised', kind: 'uncategorised', label: FIXED_LABELS.uncategorised, weight: 0 },
            lines[PAST_COMPETITIONS.length] ?? 0,
            tickets[PAST_COMPETITIONS.length] ?? 0,
          )
        }
        return [...parts.values(), fees]
      }
      case 'channel': {
        const tilt = MODEL_TILT[model] ?? {}
        return [
          ...DEMO_CHANNELS.map((channel) => ({
            key: channel.key,
            kind: 'value' as const,
            label: channel.label,
            weight: channel.weight * (tilt[channel.key] ?? 1) * wobble(seed, 0.3, 'rv-channel', channel.key, day.date),
          })),
          { key: 'unclassified', kind: 'unclassified', label: FIXED_LABELS.unclassified, weight: UNCLASSIFIED_WEIGHT },
          { key: 'unattributed', kind: 'unattributed', label: FIXED_LABELS.unattributed, weight: UNATTRIBUTED_WEIGHT },
        ]
      }
      case 'campaign': {
        const campaigns = DEMO_CAMPAIGNS.reduce((sum, campaign) => sum + campaign.weight, 0)
        return [
          ...DEMO_CAMPAIGNS.map((campaign) => ({
            key: campaign.key,
            kind: 'value' as const,
            label: campaign.key,
            weight: campaign.weight * wobble(seed, 0.35, 'rv-campaign', campaign.key, day.date),
          })),
          { key: 'no_campaign', kind: 'no_campaign', label: FIXED_LABELS.no_campaign, weight: 1 - campaigns - UNATTRIBUTED_WEIGHT },
          { key: 'unattributed', kind: 'unattributed', label: FIXED_LABELS.unattributed, weight: UNATTRIBUTED_WEIGHT },
        ]
      }
      case 'customer_type': {
        const identified = 1 - GUEST_SHARE
        return [
          { key: 'new', kind: 'value', label: CUSTOMER_TYPE_LABELS.new, weight: identified * day.newShare * 0.8 },
          { key: 'returning', kind: 'value', label: CUSTOMER_TYPE_LABELS.returning, weight: identified * (1 - day.newShare * 0.8) },
          { key: 'guest', kind: 'value', label: CUSTOMER_TYPE_LABELS.guest, weight: GUEST_SHARE * wobble(seed, 0.2, 'rv-guest', day.date) },
        ]
      }
      case 'payment_method':
        return [
          ...DEMO_PAYMENT_METHODS.map((method) => ({
            key: method.key,
            kind: 'value' as const,
            label: method.label,
            weight: method.weight * wobble(seed, 0.15, 'rv-payment', method.key, day.date),
          })),
          { key: 'unknown', kind: 'unknown', label: FIXED_LABELS.unknown, weight: UNKNOWN_PAYMENT_WEIGHT },
        ]
    }
  }

  // --- timing ---------------------------------------------------------------------------------

  timing({ filters, basis }: RevenueQuery): RevenueTimingOut {
    const frame = this.frame(filters)
    const scope = scopeFor(this.dataset.tenant, filters)
    const days = this.days(eachDay({ ...frame.range, days: frame.days }), scope)
    const previousDates = this.previousDays(frame)
    const previous = previousDates ? this.days(previousDates, scope) : null

    const grid = Array.from({ length: 168 }, (_, index) => ({ weekday: Math.floor(index / 24), hour: index % 24, revenue: 0, orders: 0 }))
    for (const day of days) {
      const weekday = weekdayOf(day.date)
      for (const part of this.hourParts(day, basis)) {
        const cell = grid[weekday * 24 + part.wallHour]
        if (!cell) continue
        cell.revenue += part.revenue
        cell.orders += part.orders
      }
    }

    const profileOf = (list: readonly DayFacts[]) =>
      Array.from({ length: 7 }, (_, weekday) => {
        const of = list.filter((day) => weekdayOf(day.date) === weekday)
        return { days: of.length, average: divideHalfUp(of.reduce((sum, day) => sum + this.revenueOf(day, basis), 0), of.length) ?? 0 }
      })
    const now = profileOf(days)
    const before = previous ? profileOf(previous) : null
    const tooShort = frame.days < 7

    const total = grid.reduce((sum, cell) => sum + cell.revenue, 0)
    let peak: { start: number; revenue: number } | null = null
    for (let start = 0; start < 168; start += 1) {
      const revenue = [0, 1, 2].reduce((sum, step) => sum + (grid[(start + step) % 168]?.revenue ?? 0), 0)
      if (!peak || revenue > peak.revenue) peak = { start, revenue }
    }

    return {
      data: {
        basis,
        compare: frame.compare,
        grid,
        weekday_profile: tooShort
          ? null
          : now.map((entry, weekday) => ({
              weekday,
              average_revenue: entry.average,
              days: entry.days,
              previous_average_revenue: before ? (before[weekday]?.average ?? null) : null,
              previous_days: before ? (before[weekday]?.days ?? null) : null,
            })),
        weekday_profile_null_reason: tooShort ? 'range_too_short' : null,
        peak:
          peak && total > 0
            ? { weekday: Math.floor(peak.start / 24), start_hour: peak.start % 24, hours: 3, revenue: peak.revenue, share: ratio4(peak.revenue, total) }
            : null,
        peak_null_reason: total > 0 ? null : 'no_revenue',
      },
      meta: this.meta(filters, basis, frame),
    }
  }
}

const COUNTS: ReadonlySet<SummaryMetricKey> = new Set(['orders', 'orders_zero_value', 'tickets_sold', 'refund_count'])

const NULL_REASONS: Partial<Record<SummaryMetricKey, SummaryMetricOut['null_reason']>> = {
  aov: 'no_orders',
  average_ticket_price: 'no_tickets',
  tickets_per_order: 'no_orders',
  refund_rate: 'no_revenue',
}
